import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Pause, Play, Warning, Check } from "@phosphor-icons/react";
import { useEffect, useState, useRef } from "react";
import { createAbandonedCart } from "@/services/api/User";
import {
  createReadyToCreateAngel,
  createVCByUser,
  checkAngelInvestorExistByName,
  createReadyToCreateAngelByGuest,
} from "@/services/api/VentureCapital";
import { useRouter } from "next/router";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
  SelectLabel,
} from "@/components/ui/select";
import { useCountry } from "@/hooks/useCountry";
import { getCityByCountry } from "@/services/api/Master";
import * as SelectPrimitive from "@radix-ui/react-select";
import { toast } from "react-hot-toast";
import { createStartupNoLogin } from "@/services/api/Startup";
import { convertStripeAmount } from "@/utils/currency";

const theme = "light";

const OnUpgradeAtStripeCheckout = ({
  isOpen,
  setIsOpen,
  currencyCode,
  handleCheckboxChange,
  handleCheckCoupon,
  handleSubscription,
  handleCouponChange,
  selectedPlan,
  priceData,
  isCoupon,
  coupon,
  couponError,
  setCouponError,
  onCancel,
  inputCoupon,
  loadingCheck,
  couponErrorProduct,
  onRemoveCoupon,
  planSubscription,
  priceDataMonthly,
  billingCycle,
  setBillingCycle,
  user,
  emailPayloadForGuest,
  setEmailPayloadForGuest,
  fullNamePayloadForGuest,
  phonePayloadForGuest,
  hideNameEmail = false,
  showStartupFields = false,
  validateStartupInfo,
  formErrors,
  setFormErrors,
}) => {
  const [hasTracked, setHasTracked] = useState(false);
  const router = useRouter();
  const [fullName, setFullName] = useState(
    user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() : (fullNamePayloadForGuest || "")
  );
  const [angelSince, setAngelSince] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [cities, setCities] = useState([]);
  const [openCountry, setOpenCountry] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const inputRef = useRef(null);
  const { countries } = useCountry();
  const [gender, setGender] = useState("");

  // Startup-specific fields
  const [startupName, setStartupName] = useState("");
  const [startupWebsite, setStartupWebsite] = useState("");

  // Loading state for payment processing
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Determine trial period text based on URL query
  const trialText = router.query.trial === "90" ? "90 Day" : "7 Day";
  const trialDays = router.query.trial === "90" ? "90" : "7";

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 50 }, (_, i) => currentYear - i);

  const menas = [
    "United Arab Emirates",
    "Saudi Arabia",
    "Egypt",
    "Nigeria",
    "South Africa",
    "India",
    "Israel",
    "Turkey",
    "Pakistan",
  ];

  const menaCountries = countries.filter((c) => menas.includes(c.country_name));
  const notMenaCountries = countries.filter(
    (c) => !menas.includes(c.country_name)
  );

  const filteredMenaCountries = menaCountries.filter((country) =>
    (country.country_name + " " + country.prefix)
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const filteredNotMenaCountries = notMenaCountries.filter((country) =>
    (country.country_name + " " + country.prefix)
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const getCity = async (id) => {
    try {
      const response = await getCityByCountry(id);
      setCities(response.data);
    } catch (error) {
      console.error("Failed to fetch cities:", error);
    }
  };

  const onCountryChange = (v) => {
    setCountry(v);
    setCity(null);
    getCity(v);
    setSearchTerm("");
  };

  const planType =
    selectedPlan === "startup"
      ? "Startup Registration"
      : selectedPlan === "vc"
        ? "VC Registration"
        : selectedPlan === "angel"
          ? "Angel Investor Registration"
          : "Content Access";

  useEffect(() => {
    if (isOpen && !hasTracked) {
      setHasTracked(true);
      handleAbandonedCart();
    }

    return () => {
      if (!isOpen) {
        setHasTracked(false);
      }
    };
  }, [isOpen]);

  useEffect(() => {
    // Get coupon from URL query params
    const { coupon: urlCoupon } = router.query;
    if (urlCoupon && !coupon) {
      // Only apply if no coupon is already set
      handleCheckboxChange(); // Enable coupon input
      handleCouponChange({ target: { value: urlCoupon } }); // Set coupon value
      handleCheckCoupon({ preventDefault: () => { } }); // Validate coupon
    }
  }, [router.query]);

  useEffect(() => {
    // Prefill email from URL query params if user is not logged in
    const { email: urlEmail } = router.query;
    if (urlEmail && !user && !emailPayloadForGuest) {
      setEmailPayloadForGuest(urlEmail);
    }
  }, [router.query, user]);

  const handleAbandonedCart = async () => {
    try {
      await createAbandonedCart({
        user_id: user?.id,
        email: user?.email || emailPayloadForGuest,
        action: "Opened Stripe Checkout Dialog",
        location: window.location.pathname,
        label:
          selectedPlan === "startup"
            ? "Startup Registration"
            : selectedPlan === "vc"
              ? "VC Registration"
              : selectedPlan === "angel"
                ? "Angel Investor Registration"
                : "Content Access",
      });
    } catch (error) {
      console.error("Failed to track abandoned cart:", error);
    }
  };

  // Add validation function
  const validateForm = () => {
    if (
      (router.pathname === "/angel-investors/register" ||
        router.pathname === "/account/angel") &&
      selectedPlan === "angel"
    ) {
      if (!hideNameEmail && !fullName?.trim()) {
        toast.error("Please enter your full name");
        return false;
      }
      if (!gender) {
        toast.error("Please select your gender");
        return false;
      }
      if (!angelSince) {
        toast.error("Please select when you started angel investing");
        return false;
      }
      if (!country) {
        toast.error("Please select your country");
        return false;
      }
      return true;
    }

    // Startup validation
    if (
      ((router.pathname === "/startups/intro" ||
        router.pathname === "/account/startup") ||
        showStartupFields) &&
      selectedPlan === "startup"
    ) {
      if (!startupName?.trim() || startupName.trim().length < 3) {
        toast.error("Startup name must be at least 3 characters");
        return false;
      }
      if (!hideNameEmail && !fullName?.trim()) {
        toast.error("Please enter your full name");
        return false;
      }
      if (!hideNameEmail && !user && !emailPayloadForGuest?.trim()) {
        toast.error("Please enter your email");
        return false;
      }
      if (!country) {
        toast.error("Please select your country");
        return false;
      }
      return true;
    }
    return true;
  };

  // Modify handleSubscriptionWithValidation to be async and handle the API call
  const handleSubscriptionWithValidation = async () => {
    if (!validateForm()) {
      return;
    }

    setIsProcessingPayment(true);

    if (
      (router.pathname === "/angel-investors/register" ||
        router.pathname === "/account/angel") &&
      selectedPlan === "angel"
    ) {
      try {
        // const payload = {
        //   full_name: fullName,
        //   angel_since: angelSince,
        //   country_id: country,
        //   city_id: city || null,
        //   user_id: user?.id
        // };

        // // Call the API to create ready-to-create angel record
        // await createReadyToCreateAngel(payload);

        // Create VC record for angel investor
        const vcPayload = {
          name: fullName,
          proposed_by: user?.id,
          proposed_owner_email: user?.email || emailPayloadForGuest,
          owning_type: "registered",
          is_angel: true,
          country_id: country,
          city_id: city || null,
          year_established: Number(angelSince),
          gender: gender,
        };

        await checkAngelInvestorExistByName({
          name: fullName,
        });

        let angelCreatedResponse;

        if (!user) {
          vcPayload.email = emailPayloadForGuest;
          angelCreatedResponse = await createReadyToCreateAngelByGuest(
            vcPayload
          );
        } else {
          angelCreatedResponse = await createVCByUser(vcPayload);
        }
        const angelId = angelCreatedResponse.data.data.id;
        const angelSlug = angelCreatedResponse.data.data.slug;

        let success_url;

        if (user) {
          success_url = `angel-investors/${angelSlug}-${angelId}/edit?stripe_success=true`;
        } else {
          success_url = `ready-to-create-angel/${angelId}`;
        }

        // If successful, proceed with subscription
        handleSubscription({
          successUrl: success_url,
          cancelUrl: "angel-investors/register",
        });
      } catch (error) {
        console.log(error);
        toast.error(
          error.response?.data?.message ||
          "Failed to process angel investor registration"
        );
        setIsProcessingPayment(false);
        return; // Don't proceed to payment if API call fails
      }
    } else if (
      ((router.pathname === "/startups/intro" ||
        router.pathname === "/account/startup") ||
        showStartupFields) &&
      selectedPlan === "startup"
    ) {
      try {
        // Validate startup information if validation function is provided
        if (validateStartupInfo) {
          const userEmail = user?.email || emailPayloadForGuest;
          const validation = await validateStartupInfo(startupName, startupWebsite, userEmail);

          if (!validation.isValid) {
            // Set form errors to display in UI instead of showing toast
            if (setFormErrors && validation.errors) {
              setFormErrors(validation.errors);
            }
            setIsProcessingPayment(false);
            return; // Don't proceed if validation fails
          }

          if (validation.message) {
            console.log("Startup validation:", validation.message);
          }
        }

        // Create startup record before proceeding to payment
        try {
          const startupResponse = await createStartupNoLogin({
            name: startupName,
            website: startupWebsite || null,
            email: user?.email || emailPayloadForGuest,
            country_id: country || null,
            city_id: city || null,
          });

          // Log the full response to understand the structure
          console.log("Startup API response:", startupResponse);

          // Check if the response indicates success
          // If there's no explicit success field, assume 200 status means success
          const hasSuccessField = startupResponse.data && typeof startupResponse.data.success !== 'undefined';
          const isSuccessful = hasSuccessField ? startupResponse.data.success : true;

          if (isSuccessful) {
            console.log("Startup created successfully before payment", startupResponse.data);
          } else {
            // Handle API errors returned in successful HTTP response
            const errorMessage = startupResponse.data?.message || "Failed to register startup";
            console.error("Startup creation failed:", errorMessage);
            toast.error(errorMessage);
            setIsProcessingPayment(false);
            return;
          }
        } catch (error) {
          console.error("Error creating startup:", error);
          const errorMessage = error.response?.data?.message || "Failed to register startup. Please try again.";
          toast.error(errorMessage);
          setIsProcessingPayment(false);
          return;
        }

        let success_url;
        if (user) {
          success_url = `startupregistration`;
        } else {
          success_url = `startupregistration`;
        }

        // If successful, proceed with subscription
        handleSubscription({
          successUrl: success_url,
          cancelUrl: "startups/intro",
        });
      } catch (error) {
        console.log(error);
        toast.error(
          error.response?.data?.message ||
          "Failed to process startup registration"
        );
        setIsProcessingPayment(false);
        return; // Don't proceed to payment if API call fails
      }
    } else {
      // For non-angel/startup plans, proceed directly to subscription
      handleSubscription();
    }
  };

  // Don't render if priceData is not available
  if (!priceData || Object.keys(priceData).length === 0) {
    return null;
  }

  return (
    <AlertDialog open={isOpen} onClose={() => setIsOpen(false)}>
      <AlertDialogContent className='max-h-full p-4 overflow-y-auto text-gray-800 bg-white'>
        <AlertDialogHeader>
          <AlertDialogTitle className='mb-3 leading-snug text-left'>
            {planSubscription && planSubscription?.active_product?.name
              ? `You are going to upgrade from the ${planSubscription?.active_product?.name} plan to the `
              : selectedPlan === "content"
                ? "Start Your Subscription for the"
                : `Start Your ${trialText} Free Trial of the`}{" "}
            {selectedPlan === "startup"
              ? "Startup Registration Plan"
              : selectedPlan === "vc"
                ? "VC Registration Plan"
                : selectedPlan === "angel"
                  ? "Angel Investor Registration Plan"
                  : "Researcher All-Access Pass"}
          </AlertDialogTitle>
          {formErrors?.general && (
            <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-md'>
              <p className='text-sm text-red-700'>{formErrors.general}</p>
            </div>
          )}
          {((router.pathname === "/startups/intro" ||
            router.pathname === "/account/startup") ||
            showStartupFields) &&
            selectedPlan === "startup" && (
              <div className=''>
                <div className='grid gap-4 text-left lg:grid-cols-2'>
                  <div className='lg:col-span-2 space-y-1.5'>
                    <Label className='text-sm text-gray-600'>
                      Startup Name<span className='text-red-700'>*</span>
                    </Label>
                    <Input
                      value={startupName}
                      onChange={(e) => setStartupName(e.target.value)}
                      className='bg-white border-gray-200 focus-visible:ring-1 focus-visible:ring-gray-300 focus-visible:border-gray-300'
                      placeholder='Enter your startup name'
                      required
                    />
                    {formErrors?.startupName && (
                      <p className='text-xs text-red-500 mt-1'>{formErrors.startupName}</p>
                    )}
                  </div>

                  <div className='lg:col-span-2 space-y-1.5'>
                    <Label className='text-sm text-gray-600'>
                      Startup Website
                    </Label>
                    <Input
                      value={startupWebsite}
                      onChange={(e) => setStartupWebsite(e.target.value)}
                      className='bg-white border-gray-200 focus-visible:ring-1 focus-visible:ring-gray-300 focus-visible:border-gray-300'
                      placeholder='https://yoursite.com (optional)'
                    />
                    {formErrors?.startupWebsite && (
                      <p className='text-xs text-red-500 mt-1'>{formErrors.startupWebsite}</p>
                    )}
                  </div>

                  {!hideNameEmail && (
                    <div className='lg:col-span-2 space-y-1.5'>
                      <Label className='text-sm text-gray-600'>
                        Your Name<span className='text-red-700'>*</span>
                      </Label>
                      <Input
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className='bg-white border-gray-200 focus-visible:ring-1 focus-visible:ring-gray-300 focus-visible:border-gray-300'
                        placeholder='Enter your full name'
                        required
                      />
                    </div>
                  )}

                  {/* Conditionally render Email input if user is not logged in */}
                  {!hideNameEmail && !user && (
                    <div className='lg:col-span-2 space-y-1.5'>
                      <Label className='text-sm text-gray-600'>
                        Email<span className='text-red-700'>*</span>
                      </Label>
                      <Input
                        type='email'
                        value={emailPayloadForGuest}
                        onChange={(e) =>
                          setEmailPayloadForGuest(e.target.value)
                        }
                        className='bg-white border-gray-200 focus-visible:ring-1 focus-visible:ring-gray-300 focus-visible:border-gray-300'
                        placeholder='Enter your email address'
                        required
                      />
                      {formErrors?.email && (
                        <p className='text-xs text-red-500 mt-1'>{formErrors.email}</p>
                      )}
                    </div>
                  )}

                  <div className='space-y-1.5'>
                    <Label className='text-sm text-gray-600'>
                      Country<span className='text-red-700'>*</span>
                    </Label>
                    <SelectPrimitive.Root
                      open={openCountry}
                      onOpenChange={setOpenCountry}
                      value={country}
                      onValueChange={onCountryChange}
                    >
                      <SelectTrigger className='bg-white border-gray-200 focus:ring-1 focus:ring-gray-300'>
                        <SelectValue placeholder='Select country' />
                      </SelectTrigger>
                      <SelectPrimitive.Content className='bg-white border border-gray-200 rounded-lg'>
                        <div className='p-1.5'>
                          <input
                            ref={inputRef}
                            type='text'
                            placeholder='Search country...'
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className='w-full px-3 py-2 text-sm border border-gray-200 rounded-md placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300'
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <ScrollArea className='w-full h-56 mx-1.5'>
                          {filteredMenaCountries.length > 0 && (
                            <SelectGroup>
                              <SelectLabel className='px-2 py-1.5 text-xs font-medium text-gray-500'>
                                MENA Region
                              </SelectLabel>
                              {filteredMenaCountries.map((country) => (
                                <SelectItem
                                  value={country.id}
                                  key={`select-country-${country.id}`}
                                  className='data-[highlighted]:bg-gray-100'
                                >
                                  <span className='text-sm'>
                                    {country.country_name} ({country.prefix})
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                          <SelectSeparator className='my-2 bg-gray-200' />
                          {filteredNotMenaCountries.length > 0 && (
                            <SelectGroup>
                              <SelectLabel className='px-2 py-1.5 text-xs font-medium text-gray-500'>
                                Other Countries
                              </SelectLabel>
                              {filteredNotMenaCountries.map((country) => (
                                <SelectItem
                                  value={country.id}
                                  key={`select-country-${country.id}`}
                                  className='data-[highlighted]:bg-gray-100'
                                >
                                  <span className='text-sm'>
                                    {country.country_name} ({country.prefix})
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                        </ScrollArea>
                      </SelectPrimitive.Content>
                    </SelectPrimitive.Root>
                  </div>

                  <div className='space-y-1.5'>
                    <Label className='text-sm text-gray-600'>City</Label>
                    <Select value={city} onValueChange={setCity} disabled={!country}>
                      <SelectTrigger className={`bg-white border-gray-200 focus:ring-1 focus:ring-gray-300 ${!country ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        <SelectValue placeholder={!country ? 'Please select a country first' : 'Select city (optional)'} />
                      </SelectTrigger>
                      {country && (
                        <SelectContent className='bg-white border-gray-200'>
                          <ScrollArea className='h-[200px] w-full p-1'>
                            <div className='pr-4'>
                              {cities.map((city) => (
                                <SelectItem
                                  value={city.id}
                                  key={`select-city-${city.id}`}
                                  className='text-gray-800 data-[highlighted]:bg-gray-100 data-[highlighted]:text-gray-800'
                                >
                                  {city.name}
                                </SelectItem>
                              ))}
                            </div>
                          </ScrollArea>
                        </SelectContent>
                      )}
                    </Select>
                  </div>
                </div>
              </div>
            )}
          {(router.pathname === "/angel-investors/register" ||
            router.pathname === "/account/angel") &&
            selectedPlan === "angel" && (
              <div className=''>
                {/* <h3 className='mb-4 text-sm font-medium text-gray-700'>
                  Angel Investor Information
                </h3> */}
                <div className='grid gap-4 text-left lg:grid-cols-2'>
                  <div className='lg:col-span-2 space-y-1.5'>
                    <Label className='text-sm text-gray-600'>
                      Name<span className='text-red-700'>*</span>
                    </Label>
                    <Input
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className='bg-white border-gray-200 focus-visible:ring-1 focus-visible:ring-gray-300 focus-visible:border-gray-300'
                      placeholder='Enter your full name'
                      required
                    />
                  </div>
                  {/* Conditionally render Email input if user is not logged in */}
                  {!user && (
                    <div className='lg:col-span-2 space-y-1.5'>
                      <Label className='text-sm text-gray-600'>
                        Email<span className='text-red-700'>*</span>
                      </Label>
                      <Input
                        type='email'
                        value={emailPayloadForGuest}
                        onChange={(e) =>
                          setEmailPayloadForGuest(e.target.value)
                        }
                        className='bg-white border-gray-200 focus-visible:ring-1 focus-visible:ring-gray-300 focus-visible:border-gray-300'
                        placeholder='Enter your email address'
                        required
                      />
                    </div>
                  )}

                  <div className='space-y-1.5'>
                    <Label className='text-sm text-gray-600'>
                      Gender<span className='text-red-700'>*</span>
                    </Label>
                    <div className='relative'>
                      <select
                        value={gender}
                        onChange={(e) => setGender(e.target.value)}
                        className='w-full h-10 px-3 py-2 text-sm bg-white border border-gray-200 rounded-md appearance-none focus:outline-none focus:ring-1 focus:ring-gray-300'
                      >
                        <option value=''>Select gender</option>
                        <option value='male'>Male</option>
                        <option value='female'>Female</option>
                        <option value='prefer_not_to_answer'>
                          Prefer not to answer
                        </option>
                      </select>
                      <div className='absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 pointer-events-none'>
                        <svg
                          className='w-4 h-4 fill-current'
                          xmlns='http://www.w3.org/2000/svg'
                          viewBox='0 0 20 20'
                        >
                          <path d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className='space-y-1.5'>
                    <Label className='text-sm text-gray-600'>
                      Active Angel Since<span className='text-red-700'>*</span>
                    </Label>
                    <div className='relative'>
                      <select
                        value={angelSince}
                        onChange={(e) => setAngelSince(e.target.value)}
                        className='w-full h-10 px-3 py-2 text-sm bg-white border border-gray-200 rounded-md appearance-none focus:outline-none focus:ring-1 focus:ring-gray-300'
                      >
                        <option value=''>Select year</option>
                        {years.map((year) => (
                          <option key={year} value={year.toString()}>
                            {year}
                          </option>
                        ))}
                      </select>
                      <div className='absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 pointer-events-none'>
                        <svg
                          className='w-4 h-4 fill-current'
                          xmlns='http://www.w3.org/2000/svg'
                          viewBox='0 0 20 20'
                        >
                          <path d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className='space-y-1.5'>
                    <Label className='text-sm text-gray-600'>
                      Country<span className='text-red-700'>*</span>
                    </Label>
                    <SelectPrimitive.Root
                      open={openCountry}
                      onOpenChange={setOpenCountry}
                      value={country}
                      onValueChange={onCountryChange}
                    >
                      <SelectTrigger className='bg-white border-gray-200 focus:ring-1 focus:ring-gray-300'>
                        <SelectValue placeholder='Select country' />
                      </SelectTrigger>
                      <SelectPrimitive.Content className='bg-white border border-gray-200 rounded-lg'>
                        <div className='p-1.5'>
                          <input
                            ref={inputRef}
                            type='text'
                            placeholder='Search country...'
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className='w-full px-3 py-2 text-sm border border-gray-200 rounded-md placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300'
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <ScrollArea className='w-full h-56 mx-1.5'>
                          {filteredMenaCountries.length > 0 && (
                            <SelectGroup>
                              <SelectLabel className='px-2 py-1.5 text-xs font-medium text-gray-500'>
                                MENA Region
                              </SelectLabel>
                              {filteredMenaCountries.map((country) => (
                                <SelectItem
                                  value={country.id}
                                  key={`select-country-${country.id}`}
                                  className='data-[highlighted]:bg-gray-100'
                                >
                                  <span className='text-sm'>
                                    {country.country_name} ({country.prefix})
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                          <SelectSeparator className='my-2 bg-gray-200' />
                          {filteredNotMenaCountries.length > 0 && (
                            <SelectGroup>
                              <SelectLabel className='px-2 py-1.5 text-xs font-medium text-gray-500'>
                                Other Countries
                              </SelectLabel>
                              {filteredNotMenaCountries.map((country) => (
                                <SelectItem
                                  value={country.id}
                                  key={`select-country-${country.id}`}
                                  className='data-[highlighted]:bg-gray-100'
                                >
                                  <span className='text-sm'>
                                    {country.country_name} ({country.prefix})
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                        </ScrollArea>
                      </SelectPrimitive.Content>
                    </SelectPrimitive.Root>
                  </div>

                  <div className='space-y-1.5'>
                    <Label className='text-sm text-gray-600'>City</Label>
                    <Select value={city} onValueChange={setCity} disabled={!country}>
                      <SelectTrigger className={`bg-white border-gray-200 focus:ring-1 focus:ring-gray-300 ${!country ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        <SelectValue placeholder={!country ? 'Please select a country first' : 'Select city (optional)'} />
                      </SelectTrigger>
                      {country && (
                        <SelectContent className='bg-white border-gray-200'>
                          <ScrollArea className='h-[200px] w-full p-1'>
                            <div className='pr-4'>
                              {cities.map((city) => (
                                <SelectItem
                                  value={city.id}
                                  key={`select-city-${city.id}`}
                                  className='text-gray-800 data-[highlighted]:bg-gray-100 data-[highlighted]:text-gray-800'
                                >
                                  {city.name}
                                </SelectItem>
                              ))}
                            </div>
                          </ScrollArea>
                        </SelectContent>
                      )}
                    </Select>
                  </div>
                </div>
              </div>
            )}
          <div>
            <div className='py-2.5'>
              <label className='flex items-center space-x-2 cursor-pointer'>
                <Checkbox
                  onCheckedChange={handleCheckboxChange}
                  checked={isCoupon}
                  className={`${theme === "light"
                      ? "border-background/20"
                      : "border-white/10"
                    }`}
                />
                <span className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'>
                  I have a coupon code
                </span>
              </label>
              {isCoupon && (
                <div className='flex items-center justify-between pt-5'>
                  <p className='text-sm font-medium'>Coupon Code (Optional)</p>
                  <div>
                    <div className='flex gap-2'>
                      <Input
                        onChange={handleCouponChange}
                        className='bg-white/5 hover:bg-white/10'
                        disabled={coupon}
                        value={inputCoupon}
                        onBlur={() => setCouponError(false)}
                      />
                      <Button
                        type='button'
                        onClick={coupon ? onRemoveCoupon : handleCheckCoupon}
                        className={`${coupon
                            ? "bg-red-500/20 hover:bg-red-500/30 text-red-400"
                            : `${theme === "light"
                              ? "bg-background/70 text-white hover:bg-background/50"
                              : "bg-white text-background"
                            }`
                          }`}
                      >
                        {loadingCheck
                          ? "Checking..."
                          : coupon
                            ? "Remove"
                            : "Apply"}
                      </Button>
                    </div>
                    {coupon && (
                      <p className='flex items-center font-medium mt-1.5 text-xs text-green-500 gap-x-1.5'>
                        <Check weight='bold' /> Coupon Applied
                      </p>
                    )}
                    {couponErrorProduct && (
                      <p className='flex items-center font-medium mt-1.5 text-xs text-red-500 gap-x-1.5'>
                        <Warning weight='bold' /> This coupon is not valid for
                        this subscription.
                      </p>
                    )}
                    {couponError && (
                      <p className='flex items-center font-medium mt-1.5 text-xs text-red-500 gap-x-1.5'>
                        <Warning weight='bold' /> This Coupon does not exist or
                        has expired.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
            {coupon && (
              <div className=''>
                <div className='flex justify-between'>
                  <p>
                    Coupon <span className='text-sm'>({coupon.id})</span>
                  </p>
                  <p>
                    -{currencyCode !== "USD" ? currencyCode + " " : "$"}
                    {coupon.percent_off
                      ? ((coupon.percent_off / 100) *
                        convertStripeAmount(priceData[
                          selectedPlan === "startup"
                            ? "Startup Registration"
                            : selectedPlan === "vc"
                              ? "VC Registration"
                              : selectedPlan === "angel"
                                ? "Angel Investor Registration"
                                : "Content Access"
                        ].unit_amount, currencyCode)
                      ).toLocaleString("en", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                      : convertStripeAmount(coupon.amount_off, currencyCode).toLocaleString("en", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </p>
                </div>
                {(selectedPlan !== "startup" && selectedPlan !== "angel") && (
                  <div className='flex justify-between mt-2 font-bold'>
                    <p>Total</p>
                    <p>
                      {currencyCode !== "USD" ? currencyCode + " " : "$"}
                      {coupon.percent_off
                        ? (
                          convertStripeAmount(priceData[
                            selectedPlan === "startup"
                              ? "Startup Registration"
                              : selectedPlan === "vc"
                                ? "VC Registration"
                                : selectedPlan === "angel"
                                  ? "Angel Investor Registration"
                                  : "Content Access"
                          ].unit_amount, currencyCode) -
                          (coupon.percent_off / 100) *
                          convertStripeAmount(priceData[
                            selectedPlan === "startup"
                              ? "Startup Registration"
                              : selectedPlan === "vc"
                                ? "VC Registration"
                                : selectedPlan === "angel"
                                  ? "Angel Investor Registration"
                                  : "Content Access"
                          ].unit_amount, currencyCode)
                        ).toLocaleString("en", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                        : (
                          convertStripeAmount(priceData[
                            selectedPlan === "startup"
                              ? "Startup Registration"
                              : selectedPlan === "vc"
                                ? "VC Registration"
                                : selectedPlan === "angel"
                                  ? "Angel Investor Registration"
                                  : "Content Access"
                          ].unit_amount, currencyCode) -
                          convertStripeAmount(coupon.amount_off, currencyCode)
                        ).toLocaleString("en", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* {(selectedPlan === "startup" || selectedPlan === "angel") && router.pathname !== "/startups/intro" && (
              <div className='flex items-center justify-between pt-2.5 mt-2.5 border-gray-200 border-t'>
                <p className='text-sm'>
                  {coupon && Object.keys(coupon).length > 0
                    ? "Price after discount (After trial)"
                    : "Regular Price (After Trial)"}
                </p>
                <p className='text-base font-medium'>
                  {currencyCode !== "USD" ? currencyCode + " " : "$"}
                  {coupon && Object.keys(coupon).length > 0
                    ? (coupon.percent_off
                        ? (
                            convertStripeAmount(priceData[
                              selectedPlan === "startup"
                                ? "Startup Registration"
                                : "Angel Investor Registration"
                            ].unit_amount, currencyCode) -
                            (coupon.percent_off / 100) *
                              convertStripeAmount(priceData[
                                selectedPlan === "startup"
                                  ? "Startup Registration"
                                  : "Angel Investor Registration"
                              ].unit_amount, currencyCode)
                          ).toLocaleString("en", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : (
                            convertStripeAmount(priceData[
                              selectedPlan === "startup"
                                ? "Startup Registration"
                                : "Angel Investor Registration"
                            ].unit_amount, currencyCode) -
                            convertStripeAmount(coupon.amount_off, currencyCode)
                          ).toLocaleString("en", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }))
                    : convertStripeAmount(
                        priceData[
                          selectedPlan === "startup"
                            ? "Startup Registration"
                            : "Angel Investor Registration"
                        ].unit_amount,
                        currencyCode
                      ).toLocaleString("en", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                </p>
              </div>
            )} */}

            <div className='flex items-center justify-between pt-2.5 mt-2.5 border-gray-200 border-t mb-2.5'>
              <p className='text-sm'>
                {(selectedPlan === "startup" || selectedPlan === "angel")
                  ? `Trial Price (${trialDays} Days)`
                  : "Price"}
              </p>
              <p className='text-lg font-bold lg:text-xl'>
                {currencyCode !== "USD" ? currencyCode + " " : "$"}
                {(selectedPlan === "startup" || selectedPlan === "angel") ? "0.00" :
                  convertStripeAmount(priceData[
                    selectedPlan === "startup"
                      ? "Startup Registration"
                      : selectedPlan === "vc"
                        ? "VC Registration"
                        : selectedPlan === "angel"
                          ? "Angel Investor Registration"
                          : "Content Access"
                  ].unit_amount, currencyCode).toLocaleString("en", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
              </p>
            </div>

            {(router.pathname === "/angel-investors/register" ||
              router.pathname === "/account/angel") &&
              selectedPlan === "angel" && (
                <p className='text-[11px] text-gray-500 mb-2.5'>
                  <strong>Note:</strong> After your {trialDays}-day free trial ends, you will be automatically charged {currencyCode !== "USD" ? currencyCode + " " : "$"}
                  {coupon && Object.keys(coupon).length > 0
                    ? (coupon.percent_off
                      ? (
                        convertStripeAmount(priceData["Angel Investor Registration"].unit_amount, currencyCode) -
                        (coupon.percent_off / 100) * convertStripeAmount(priceData["Angel Investor Registration"].unit_amount, currencyCode)
                      ).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : (
                        convertStripeAmount(priceData["Angel Investor Registration"].unit_amount, currencyCode) -
                        convertStripeAmount(coupon.amount_off, currencyCode)
                      ).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
                    : convertStripeAmount(priceData["Angel Investor Registration"].unit_amount, currencyCode).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  } for your 1 Year Subscription.
                </p>
              )}

            {((router.pathname === "/startups/intro" ||
              router.pathname === "/account/startup") ||
              showStartupFields) &&
              selectedPlan === "startup" && (
                <p className='text-[11px] text-gray-500 mb-2.5'>
                  <strong>Note:</strong> After your {trialDays}-day free trial ends, you will be automatically charged {currencyCode !== "USD" ? currencyCode + " " : "$"}
                  {coupon && Object.keys(coupon).length > 0
                    ? (coupon.percent_off
                      ? (
                        convertStripeAmount(priceData["Startup Registration"].unit_amount, currencyCode) -
                        (coupon.percent_off / 100) * convertStripeAmount(priceData["Startup Registration"].unit_amount, currencyCode)
                      ).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : (
                        convertStripeAmount(priceData["Startup Registration"].unit_amount, currencyCode) -
                        convertStripeAmount(coupon.amount_off, currencyCode)
                      ).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
                    : convertStripeAmount(priceData["Startup Registration"].unit_amount, currencyCode).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  } for your 1 Year Subscription.
                </p>
              )}

            {planType === "Content Access" && (
              <div className='flex items-center justify-between py-4'>
                <p className='text-sm font-medium'>Billing Cycle</p>
                <div className='flex items-center gap-x-1'>
                  <Button
                    type='button'
                    onClick={() => setBillingCycle("yearly")}
                    className={`border text-xs h-auto py-1.5 px-3 gap-x-1.5 ${theme === "light"
                        ? `text-background border-background/10 ${billingCycle === "yearly"
                          ? "bg-background text-white hover:bg-background/80"
                          : "bg-background/5 hover:bg-background/10 text-background"
                        }`
                        : `text-white border-white/10 ${billingCycle === "yearly"
                          ? "bg-white text-background"
                          : "bg-white/5 hover:bg-white/10"
                        }`
                      }`}
                  >
                    <span
                      className={`w-3.5 h-3.5 border inline-flex justify-center items-center rounded-full ${theme === "light"
                          ? `text-background border-background/10 ${billingCycle === "yearly"
                            ? "bg-green-500 text-white border-green-500"
                            : "bg-background/5 border-background/5"
                          }`
                          : `text-white border-white/10 ${billingCycle === "yearly"
                            ? "bg-green-500 text-white border-green-500"
                            : "bg-white/5 border-white/5"
                          }`
                        }`}
                    >
                      {billingCycle === "yearly" && (
                        <Check weight='bold' className='text-[9px]' />
                      )}
                    </span>
                    Yearly (20% OFF)
                  </Button>
                  <Button
                    type='button'
                    onClick={() => setBillingCycle("monthly")}
                    className={`border text-xs h-auto py-1.5 px-3 gap-x-1.5 ${theme === "light"
                        ? `text-background border-background/10 ${billingCycle === "monthly"
                          ? "bg-background text-white hover:bg-background/80"
                          : "bg-background/5 hover:bg-background/10 text-background"
                        }`
                        : `text-white border-white/10 ${billingCycle === "monthly"
                          ? "bg-white text-background"
                          : "bg-background/5 hover:bg-background/10"
                        }`
                      }`}
                  >
                    <span
                      className={`w-3.5 h-3.5 border inline-flex justify-center items-center rounded-full ${theme === "light"
                          ? `text-background border-background/10 ${billingCycle === "monthly"
                            ? "bg-green-500 text-white border-green-500"
                            : "bg-background/5 border-background/5"
                          }`
                          : `text-white border-white/10 ${billingCycle === "monthly"
                            ? "bg-green-500 text-white border-green-500"
                            : "bg-white/5 border-white/5"
                          }`
                        }`}
                    >
                      {billingCycle === "monthly" && (
                        <Check weight='bold' className='text-[9px]' />
                      )}
                    </span>
                    Monthly
                  </Button>
                </div>
              </div>
            )}
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter className='flex sm:justify-end'>
          <AlertDialogCancel
            onClick={onCancel}
            className='bg-white border-gray-200 hover:bg-gray-100'
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleSubscriptionWithValidation();
            }}
            disabled={isProcessingPayment}
            className='text-white bg-gray-800 hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {isProcessingPayment ? (
              <div className='flex items-center gap-2'>
                <svg className='animate-spin -ml-1 mr-2 h-4 w-4 text-white' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                  <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                  <path className='opacity-75' fill='currentColor' d='m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
                </svg>
                Processing...
              </div>
            ) : (
              'Go to Payment'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default OnUpgradeAtStripeCheckout;
